import React from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';

const YEAR = new Date().getFullYear();

// The frame for sign-in, registration and the password screens.
//
// People here are already customers (or about to become one), so it carries the brand and a way
// to get help — the real Rostery wordmark, one plain line about the product, support contact,
// system status and the company. No marketing numbers nobody can check, no glow, no animation.
// On a phone the dark panel is dropped and the wordmark sits above the form.
export default function AuthShell({ children, note, wide = false }) {
  return (
    <div className="min-h-screen flex bg-white brand-cta">
      {/* Deep brand indigo (trust, calm competence) with a soft glow, and the app's section
          colours along the bottom: the same hues people meet inside, one per area of work. */}
      <aside className="hidden lg:flex lg:w-[42%] xl:w-[40%] max-w-[640px] shrink-0 sticky top-0 h-screen flex-col justify-between overflow-hidden px-12 py-10 bg-gradient-to-br from-indigo-950 via-indigo-900 to-violet-900">
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-indigo-500/25 blur-3xl" />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -left-24 w-[26rem] h-[26rem] rounded-full bg-teal-400/15 blur-3xl" />
        <img src="/logo-admin.png" alt="Rostery" className="relative h-10 w-auto self-start" />
        <div className="relative max-w-sm">
          <p className="text-[26px] font-semibold leading-snug tracking-[-0.01em] text-white [text-wrap:balance]">
            Rostering, billing and compliance for NDIS and aged care providers.
          </p>
          {note && <div className="mt-4 text-sm leading-relaxed text-indigo-200">{note}</div>}
          <ul className="mt-8 flex flex-wrap gap-x-4 gap-y-2 text-[12px] text-indigo-100/90" aria-label="What's inside">
            {[
              ['Scheduling', 'bg-blue-400'], ['People', 'bg-orange-400'], ['Care & quality', 'bg-pink-400'],
              ['Aged care', 'bg-teal-400'], ['Finance', 'bg-emerald-400'], ['Insights', 'bg-amber-400'],
            ].map(([name, dot]) => (
              <li key={name} className="inline-flex items-center gap-1.5">
                <span className={clsx('w-2 h-2 rounded-full', dot)} aria-hidden="true" />{name}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative"><SupportFooter dark /></div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <main className="flex-1 flex items-start sm:items-center justify-center px-4 sm:px-8 py-10">
          <div className={clsx('w-full', wide ? 'max-w-md' : 'max-w-sm')}>
            {/* The wordmark is white (it is drawn for the dark rail), so it is shown dark here. */}
            <img src="/logo-admin.png" alt="Rostery" className="h-9 w-auto brightness-0 mb-8 lg:hidden" />
            {children}
          </div>
        </main>
        <div className="lg:hidden px-4 pb-6">
          <SupportFooter />
        </div>
      </div>
    </div>
  );
}

function SupportFooter({ dark = false }) {
  const link = dark ? 'text-indigo-100 hover:text-white' : 'text-slate-600 hover:text-slate-900';
  return (
    <footer className={clsx('text-xs leading-relaxed space-y-1', dark ? 'text-indigo-200' : 'text-slate-500 text-center')}>
      <p>
        Need help? Email{' '}
        <a href="mailto:info@rostery.com.au" className={clsx('underline underline-offset-2', link)}>info@rostery.com.au</a>
      </p>
      <p>
        <Link to="/status" className={clsx('underline underline-offset-2', link)}>System status</Link>
        {' · '}© {YEAR} Notion Tech Pty Ltd
      </p>
    </footer>
  );
}

// A problem with the whole form (wrong password, server unreachable) — shown above the submit
// button, where the person is looking, and announced to screen readers.
export function FormAlert({ children, className, id }) {
  return (
    <div id={id} role="alert" className={clsx('rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm leading-snug text-red-700', className)}>
      {children}
    </div>
  );
}

// The screen's title block.
export function AuthHeading({ title, children, className }) {
  return (
    <div className={clsx('mb-7', className)}>
      <h1 className="text-[26px] font-semibold tracking-[-0.02em] text-slate-900 [text-wrap:balance]">{title}</h1>
      {children && <p className="mt-1.5 text-sm leading-relaxed text-slate-500">{children}</p>}
    </div>
  );
}
